import React, { useMemo, useState } from "react";

function normalizeWebsiteUrl(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export default function ClubListNA({ clubs = [] }) {
  const [sortKey, setSortKey] = useState("nom"); // "nom" | "dep"

  const sorted = useMemo(() => {
    const arr = [...clubs];
    if (sortKey === "dep") {
      arr.sort((a, b) => {
        const d = String(a.departement_club || "").localeCompare(
          String(b.departement_club || ""),
        );
        if (d !== 0) return d;
        return String(a.nom_club || "").localeCompare(String(b.nom_club || ""));
      });
    } else {
      arr.sort((a, b) =>
        String(a.nom_club || "").localeCompare(String(b.nom_club || "")),
      );
    }
    return arr;
  }, [clubs, sortKey]);

  return (
    <div className="rounded-xl ring-1 ring-slate-200 bg-white">
      <div className="px-4 py-3 flex flex-wrap items-center gap-2">
        <div className="text-sm opacity-80">Affichage en liste</div>
        <div className="ml-auto">
          <div className="inline-flex rounded-lg ring-1 ring-slate-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setSortKey("nom")}
              className={`px-3 py-1.5 text-sm ${sortKey === "nom" ? "bg-indigo-600 text-white" : "bg-white hover:bg-slate-50"}`}
              title="Trier par nom"
            >
              Trier par nom
            </button>
            <button
              type="button"
              onClick={() => setSortKey("dep")}
              className={`px-3 py-1.5 text-sm ${sortKey === "dep" ? "bg-indigo-600 text-white" : "bg-white hover:bg-slate-50"}`}
              title="Trier par département"
            >
              Trier par département
            </button>
          </div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="px-4 pb-4 text-sm opacity-70">
          Aucun club avec ces filtres.
        </div>
      ) : (
        <div className="px-4 pb-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {sorted.map((c) => {
            const coords = (c.coordonnees_gps || "").split(",");
            const lat = parseFloat(coords[0]);
            const lon = parseFloat(coords[1]);
            const hasCoords = Number.isFinite(lat) && Number.isFinite(lon);
            const websiteUrl = normalizeWebsiteUrl(c.site_club);

            return (
              <article
                key={c.id || c.nom_club}
                className="rounded-xl ring-1 ring-slate-200 p-4 bg-white hover:shadow-sm transition cursor-default"
                onClickCapture={(e) => {
                  if (e.target.closest('[data-action="club-action"]')) return;
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <div className="flex items-start gap-3">
                  <img
                    src={c.logo_url || "/img/no-image.jpg"}
                    alt=""
                    className="w-14 h-14 rounded-lg object-cover ring-1 ring-slate-200 bg-white"
                  />
                  <div className="min-w-0">
                    <h3 className="font-semibold leading-5">
                      {c.nom_club || "—"}
                    </h3>
                    <div className="mt-1 text-xs inline-flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100">
                        {c.departement_club || "—"}
                      </span>
                      {c.numero_club && (
                        <span className="px-2 py-0.5 rounded-full bg-slate-100">
                          N° {c.numero_club}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-sm text-slate-700 space-y-1">
                  {c.adresse_club && <div>📍 {c.adresse_club}</div>}
                  {c.tel_club && <div>📞 {c.tel_club}</div>}
                  {c.email_club && (
                    <div className="truncate">
                      ✉️ <span className="text-slate-700">{c.email_club}</span>
                    </div>
                  )}
                  {websiteUrl && (
                    <div className="truncate">
                      🌐{" "}
                      <a
                        href={websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-action="club-action"
                        onClick={(e) => e.stopPropagation()}
                        className="text-indigo-600 hover:underline"
                      >
                        {c.site_club}
                      </a>
                    </div>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {hasCoords && (
                    <button
                      type="button"
                      data-action="club-action"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lon}`)}`;
                        window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 cursor-pointer"
                    >
                      Ouvrir dans Maps
                    </button>
                  )}

                  {c.email_club && (
                    <button
                      type="button"
                      data-action="club-action"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = `mailto:${c.email_club}`;
                      }}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm hover:bg-slate-50 cursor-pointer"
                    >
                      Contacter
                    </button>
                  )}

                  {websiteUrl && (
                    <button
                      type="button"
                      data-action="club-action"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(websiteUrl, "_blank", "noopener,noreferrer");
                      }}
                      className="px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-700 text-sm hover:bg-indigo-50 cursor-pointer"
                    >
                      Visiter le site
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}